import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { useJsonLd, usePageMeta } from "@/hooks/usePageMeta";
import { articles } from "@/mocks/articles";
import { getTopicName } from "@/mocks/topics";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((item) => item.id === id);

  usePageMeta({
    title: article
      ? `${article.title} | 吃瓜深扒社`
      : "内容不存在 | 吃瓜深扒社",
    description: article
      ? article.excerpt
      : "该篇选美吃瓜解析内容暂时不存在，你可以返回专题列表查看全部赛果复盘。",
    keywords: article
      ? `${article.tags.join(",")},选美吃瓜,选美吃瓜内定,选美吃瓜黑幕`
      : "选美吃瓜,选美吃瓜内定,选美吃瓜黑幕",
    path: `/article/${id ?? ""}`,
  });

  useJsonLd(
    "article",
    article
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": `https://dstcm.cn/article/${article.id}#article`,
              headline: article.title,
              description: article.excerpt,
              image: article.cover,
              datePublished: article.date,
              dateModified: article.date,
              inLanguage: "zh-CN",
              keywords: article.tags.join(","),
              articleSection: getTopicName(article.category),
              author: {
                "@type": "Organization",
                name: article.author,
                url: "https://dstcm.cn/about",
              },
              publisher: {
                "@type": "Organization",
                name: "吃瓜深扒社",
                url: "https://dstcm.cn/",
              },
              mainEntityOfPage: `https://dstcm.cn/article/${article.id}`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://dstcm.cn/" },
                { "@type": "ListItem", position: 2, name: "爆料专题", item: "https://dstcm.cn/topics" },
                { "@type": "ListItem", position: 3, name: article.title, item: `https://dstcm.cn/article/${article.id}` },
              ],
            },
          ],
        }
      : { "@context": "https://schema.org", "@type": "WebPage", name: "内容不存在" },
  );

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="max-w-lg text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300" aria-hidden="true"></i>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
              这篇内容暂时不在
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              可能是链接有误，或者这篇解析还在核对中。你可以返回专题列表查看全部已发布内容。
            </p>
            <Link
              to="/topics"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回爆料专题
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = articles
    .filter((item) => item.id !== article.id && item.category === article.category)
    .slice(0, 2);
  const fallbackRelated = articles.filter((item) => item.id !== article.id).slice(0, 2);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <Breadcrumbs
          items={[
            { label: "首页", to: "/" },
            { label: "爆料专题", to: "/topics" },
            { label: getTopicName(article.category), to: `/topics?topic=${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-4 py-1.5 font-label text-xs text-secondary-900">
                <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                {getTopicName(article.category)}
              </span>
              <h1 className="mt-5 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                {article.title}
              </h1>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line" aria-hidden="true"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  <time dateTime={article.date}>{article.date}</time>
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  约 {article.readMinutes} 分钟
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                  热度 {article.heat}
                </span>
              </div>

              <div className="mt-8 h-56 w-full overflow-hidden rounded-lg border border-background-200 md:h-80">
                <img
                  src={article.cover}
                  alt={`${article.title} - 选美吃瓜赛果解析配图`}
                  title={`${article.title} - 选美吃瓜`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <p className="mt-8 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5 text-sm leading-loose text-primary-950 md:text-base">
                {article.lead}
              </p>

              {article.sections.map((section, index) => (
                <section key={section.heading} id={`section-${index + 1}`} className="mt-10 scroll-mt-24">
                  <h2 className="flex items-start gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    <span className="mt-1 font-heading text-accent-500">{String(index + 1).padStart(2, "0")}</span>
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph.slice(0, 24)} className="text-sm leading-loose text-foreground-800 md:text-base">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  {section.list && (
                    <ul className="mt-5 space-y-3 rounded-lg border border-background-200 bg-background-100 p-5">
                      {section.list.map((item) => (
                        <li key={item} className="flex gap-3 text-sm leading-relaxed text-foreground-800">
                          <i className="ri-checkbox-line mt-0.5 text-base text-primary-600" aria-hidden="true"></i>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              <div id="verdict" className="mt-12 scroll-mt-24 rounded-lg border border-accent-300 bg-accent-50 p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-accent-950">
                  <i className="ri-lightbulb-line" aria-hidden="true"></i>
                  深扒结论
                </h2>
                <p className="mt-3 text-sm leading-loose text-accent-950 md:text-base">
                  {article.verdict}
                </p>
              </div>

              <div className="mt-8">
                <h2 className="font-label text-sm text-foreground-600">相关标签</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/topics?q=${encodeURIComponent(tag)}`}
                      className="rounded-full border border-background-300 px-3 py-1.5 font-label text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <nav aria-label="内容目录" className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-list-unordered text-primary-600" aria-hidden="true"></i>
                    内容目录
                  </h2>
                  <ul className="mt-4 space-y-1">
                    {article.sections.map((section, index) => (
                      <li key={section.heading}>
                        <a
                          href={`#section-${index + 1}`}
                          className="flex gap-3 rounded-md px-3 py-2 text-sm leading-relaxed text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                        >
                          <span className="font-label text-xs text-accent-600">{String(index + 1).padStart(2, "0")}</span>
                          {section.heading}
                        </a>
                      </li>
                    ))}
                    <li>
                      <a
                        href="#verdict"
                        className="flex gap-3 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                      >
                        <span className="font-label text-xs text-accent-600">04</span>
                        深扒结论
                      </a>
                    </li>
                  </ul>
                </nav>

                <div className="mt-5 rounded-lg bg-secondary-100 p-5">
                  <h2 className="font-heading text-base font-bold text-secondary-950">有更完整的物料？</h2>
                  <p className="mt-2 text-sm leading-relaxed text-secondary-900">
                    如果你手上有这篇内容涉及到的公示照片、通稿原文或票数记录，欢迎投递，我们会做交叉验证后更新本文。
                  </p>
                  <Link
                    to="/about#contact"
                    className="mt-4 flex items-center justify-center gap-2 rounded-md bg-secondary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-secondary-600 whitespace-nowrap"
                  >
                    <i className="ri-mail-send-line" aria-hidden="true"></i>
                    投递线索
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-16 border-t border-background-200 pt-10">
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                相关推荐
              </h2>
              <Link
                to="/topics"
                className="flex items-center gap-1 font-label text-sm text-foreground-700 transition-colors hover:text-primary-600"
              >
                回到专题
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {relatedList.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </section>
        </article>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}